import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Loader2, X, UserCheck, UserPlus, Mail, ExternalLink } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { hasSponsorForm, sponsorRoleLabel, sponsorRoleNoun } from '../lib/schema-runtime';

interface SponsorContact {
  id: string;
  given_names: string | null;
  family_name: string | null;
  email: string;
}

interface Props {
  matterId: string;
  /** Visa subclass — drives whether a sponsor role applies at all. */
  visaSubclass: string;
  /** Currently-linked sponsor, or null if none yet. */
  sponsor: SponsorContact | null;
  onSaved: () => void;
}

// Same format guards used in CreateMatter — a mistyped email/phone must never
// link the wrong person or fire a welcome email to a bad address.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s()-]{7,}$/;

/**
 * Staff-only control to add the matter's second party where there is none, or
 * to replace/correct the linked one at any time. Resolves an existing contact
 * by email (or creates a new one), writes matters.sponsor_contact_id, then
 * re-triggers the welcome so the newly-linked party gets their login. Mirrors
 * the resolve-or-create pattern in CreateMatter.tsx and the welcome re-trigger
 * pattern in MatterReadiness.tsx.
 *
 * The party is called "Inviter" on the 600 tourist stream and "Sponsor"
 * everywhere else (sponsorRoleLabel). On 600-tourist the Add/Change control is
 * ENABLED and optional: before Wave 5 that subclass fell into the read-only
 * "no separate sponsor form applies" branch below, which is now reached only by
 * 461 and 020.
 */
export default function SponsorPanel({ matterId, visaSubclass, sponsor, onSaved }: Props) {
  // Vocabulary: the 600 tourist stream calls this party the Inviter; every
  // other sponsor-bearing subclass keeps Sponsor. Label only: the column, the
  // contact_type and the edge function are all still "sponsor".
  const roleLabel = sponsorRoleLabel(visaSubclass);
  const roleNoun = sponsorRoleNoun(visaSubclass);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [givenNames, setGivenNames] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [phone, setPhone] = useState('');
  const [resolved, setResolved] = useState<SponsorContact | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'searching' | 'found' | 'new'>('idle');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setEmail('');
    setGivenNames('');
    setFamilyName('');
    setPhone('');
    setResolved(null);
    setLookupState('idle');
    setError(null);
  };

  // Debounced email lookup, same shape as CreateMatter's. Kept inline (one
  // caller) rather than extracted to keep the change surgical.
  const onEmailChange = (value: string) => {
    setEmail(value);
    setResolved(null);
    const trimmed = value.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
      setLookupState('idle');
      return;
    }
    setLookupState('searching');
  };

  const runLookup = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) return;
    setLookupState('searching');
    const { data } = await supabase
      .from('contacts')
      .select('id, given_names, family_name, email')
      .eq('email', trimmed)
      .maybeSingle();
    if (data) {
      setResolved(data as SponsorContact);
      setLookupState('found');
    } else {
      setResolved(null);
      setLookupState('new');
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail) throw new Error(`${roleLabel} email is required.`);
      if (!EMAIL_RE.test(trimmedEmail)) {
        throw new Error(`That ${roleNoun} email does not look right. Check for typos before continuing.`);
      }
      if (!resolved && (!givenNames.trim() || !familyName.trim())) {
        throw new Error(`${roleLabel} given names and family name are required.`);
      }
      if (phone.trim() && !PHONE_RE.test(phone.trim())) {
        throw new Error(`That ${roleNoun} phone number does not look right. Use digits with an optional leading +.`);
      }

      // Resolve-or-create the sponsor contact. Existing contacts use their id;
      // new ones get inserted. Re-check by email at save time (race-safety),
      // same as CreateMatter.
      let sponsorContactId: string;
      if (resolved) {
        sponsorContactId = resolved.id;
      } else {
        const { data: existing } = await supabase
          .from('contacts')
          .select('id')
          .eq('email', trimmedEmail)
          .maybeSingle();
        if (existing) {
          sponsorContactId = existing.id;
        } else {
          const { data: created, error: createErr } = await supabase
            .from('contacts')
            .insert({
              email: trimmedEmail,
              given_names: givenNames.trim() || null,
              family_name: familyName.trim() || null,
              phone: phone.trim() || null,
              contact_type: 'sponsor',
            })
            .select('id')
            .single();
          if (createErr) throw createErr;
          sponsorContactId = created.id;
        }
      }

      // Don't re-link/re-welcome if it's already the linked sponsor.
      const isNewLink = sponsor?.id !== sponsorContactId;

      const { error: updateErr } = await supabase
        .from('matters')
        .update({ sponsor_contact_id: sponsorContactId } as never)
        .eq('id', matterId);
      if (updateErr) throw updateErr;

      // Fire the sponsor welcome for a newly-linked sponsor. The DB webhook
      // that auto-sends on matter INSERT does NOT fire on this UPDATE, so we
      // re-trigger explicitly using the same synth-payload pattern as the
      // MatterReadiness quick-fix. Best-effort: a failure here doesn't undo
      // the link.
      if (isNewLink) {
        await sendSponsorWelcome(matterId);
      }

      setOpen(false);
      reset();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to update ${roleNoun}.`);
    } finally {
      setSaving(false);
    }
  };

  const sponsorName = sponsor
    ? [sponsor.given_names, sponsor.family_name].filter(Boolean).join(' ').trim() || sponsor.email
    : null;

  // Visas with no sponsor form (e.g. 461 NZ Citizen Family Relationship) get no
  // add/change control — there is no 40SP for them. Any legacy-linked sponsor is
  // still shown read-only so staff can see it.
  if (!hasSponsorForm(visaSubclass)) {
    return (
      <div className="py-1.5 text-sm">
        <div className="text-xs text-slate-500">{roleLabel}</div>
        {sponsor ? (
          <Link
            to={`/contacts/${sponsor.id}`}
            className="font-medium text-[#0B2341] hover:text-[#2CABEA] hover:underline block"
          >
            {sponsorName}
          </Link>
        ) : (
          <div className="text-slate-500">
            {visaSubclass === '461'
              ? "No sponsor for this visa. The New Zealand citizen's details are captured in the applicant's Form 147 (Parts E and F)."
              : 'No separate sponsor form applies to this visa subclass.'}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="py-1.5 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">{roleLabel}</div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-slate-400 hover:text-[#2CABEA] inline-flex items-center gap-1 text-xs"
            title={sponsor ? `Change ${roleNoun}` : `Add ${roleNoun}`}
            aria-label={sponsor ? `Change ${roleNoun}` : `Add ${roleNoun}`}
          >
            <Pencil size={11} /> {sponsor ? 'Change' : 'Add'}
          </button>
        )}
      </div>

      {sponsor ? (
        <>
          <Link
            to={`/contacts/${sponsor.id}`}
            className="font-medium text-[#0B2341] hover:text-[#2CABEA] hover:underline block"
          >
            {sponsorName}
          </Link>
          <a
            href={`mailto:${sponsor.email}`}
            className="text-xs text-slate-500 hover:text-[#2CABEA] inline-flex items-center gap-1"
          >
            <Mail size={10} /> {sponsor.email}
          </a>
        </>
      ) : (
        !open && <div className="text-slate-400">Not linked</div>
      )}

      {open && (
        <div className="mt-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-600">
              {sponsor ? `Change ${roleNoun}` : `Add ${roleNoun}`}
            </span>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                reset();
              }}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Cancel"
            >
              <X size={12} />
            </button>
          </div>

          <div>
            <label className="form-label">Email</label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                onBlur={runLookup}
                className="form-input pr-9"
                placeholder={`${roleNoun}@example.com`}
                disabled={!!resolved}
              />
              {lookupState === 'searching' && (
                <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
              )}
            </div>
            <p className="form-hint">They'll use this to sign in to the portal.</p>
          </div>

          {resolved && (
            <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-2 flex items-start gap-2">
              <UserCheck size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-emerald-900">
                  Existing contact:{' '}
                  {[resolved.given_names, resolved.family_name].filter(Boolean).join(' ') || resolved.email}
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <Link
                    to={`/contacts/${resolved.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                  >
                    <ExternalLink size={11} /> View profile
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setResolved(null);
                      setEmail('');
                      setLookupState('idle');
                    }}
                    className="text-slate-500 hover:underline"
                  >
                    Use a different {roleNoun}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!resolved && lookupState === 'new' && (
            <div className="rounded-lg border-2 border-slate-200 bg-slate-50/40 p-2 space-y-2">
              <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                <UserPlus size={14} /> New {roleNoun} - fill in their details
              </div>
              <div>
                <label className="form-label">Given names</label>
                <input
                  type="text"
                  value={givenNames}
                  onChange={(e) => setGivenNames(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Family name</label>
                <input
                  type="text"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="form-input"
                  placeholder="+61 ..."
                />
              </div>
            </div>
          )}

          {error && <div className="text-red-700">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                reset();
              }}
              className="text-slate-500 hover:text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || lookupState === 'idle' || lookupState === 'searching'}
              className="btn-primary !py-1.5 !px-3 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 size={12} className="animate-spin" /> Saving…
                </>
              ) : sponsor ? (
                `Save ${roleNoun}`
              ) : (
                `Link ${roleNoun}`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Re-trigger the sponsor welcome email for a matter whose sponsor was just
 * linked via UPDATE (the auto-send DB webhook only fires on matter INSERT).
 * Synthesises an INSERT-shaped payload and POSTs to the edge function, the
 * same approach the MatterReadiness quick-fix uses. Best-effort.
 */
async function sendSponsorWelcome(matterId: string): Promise<void> {
  try {
    const portalBase = (import.meta.env.VITE_SUPABASE_URL as string) ?? '';
    const sessionRes = await supabase.auth.getSession();
    const jwt = sessionRes.data.session?.access_token;
    if (!portalBase || !jwt) return;

    // Clear any prior 'sent' sponsor-welcome notification so the function
    // doesn't dedupe-skip the re-send.
    await supabase
      .from('notifications')
      .delete()
      .eq('matter_id', matterId)
      .eq('template_key', 'welcome-sponsor');

    const { data } = await supabase
      .from('matters')
      .select('id, visa_subclass, variant, primary_contact_id, sponsor_contact_id, assigned_staff_id, status')
      .eq('id', matterId)
      .maybeSingle();
    if (!data) return;

    await fetch(`${portalBase}/functions/v1/send-sponsor-welcome`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        type: 'INSERT',
        table: 'matters',
        schema: 'public',
        record: data,
        old_record: null,
      }),
    }).catch(() => null);
  } catch {
    // Best-effort: linking the sponsor already succeeded.
  }
}
